<template>
    <a href="javascript:" class="spi__container inline-block" tabindex="0" role="button" :aria-label="item.label"
       :class="colorClass">
        <div class="spi__check-mark"
             v-if="showCheckMark"
             :class="{'spi__check-mark_yellow': checkMarkYellow}"
        ><i class="fa fa-check" aria-hidden="true"></i></div>
        <div class="spi__button">
            <div class="spi__title" v-html="item.label"></div>
            <div class="spi__icon-section" :class="item.customIcon">
                <i :class="[item.iconPrefix || 'fa', 'spi__fa-icon', item.iconClass]"
                   v-if="item.iconClass"
                   aria-hidden="true"
                ></i>
            </div>
        </div>
    </a>
</template>

<script>
    export default {
        name: 'SelectorPopupItem',
        props: {
            item: Object,
        },
        computed: {
            colorClass() {
                if (!this.item.color) {
                    return 'spi__container_blue';
                } else {
                    return `spi__container_${this.item.color}`;
                }
            },
            showCheckMark() {
                return this.item.checkMark;
            },
            checkMarkYellow() {
                return this.item.checkMark && this.item.checkMarkYellow;
            }
        }

    };
</script>

<style scoped lang="scss">

  @use "../../../angular/app/styles/const" as *;
  @use "../../../angular/app/styles/mixins" as *;

  .spi {

    &__check-mark {
      display: block;
      position: absolute;
      right: -19px;
      top: -8px;
      z-index: 10;

      background-color: $grass-green;

      // after the block's own background, not before it: a modifier is a second class on
      // the same element, so it only wins by coming later in the output, and dart-sass
      // emits a declaration written below a nested rule after that rule - see
      // ChatStyleComment.vue, where the same order made every own comment white
      &_yellow {
        background-color: $yellow;
      }

      width: 40px;
      height: 40px;
      border-radius: 50%;
      text-align: center;
      // stylelint-disable-next-line declaration-property-unit-disallowed-list -- icon sized with its box
      line-height: 40px;
      // stylelint-disable-next-line declaration-property-unit-disallowed-list -- icon sized with its box
      font-size: 40px;
      color: var(--color-on-fill);
    }

    &__container {
      position: relative;
      display: inline-block;
      border-radius: 5px;
      margin: 5px;
      padding: 5px;
      min-height: 7.5rem;
      // in rem, so the title and the icon below it keep their room at a larger text size
      width: 7.5rem;
      text-decoration: none;

      @mixin panelColors($basicColor) {
        background-color: $basicColor;

        &:focus, &:active {
          background-color: color-mix(in srgb, $basicColor, var(--color-surface) 10%);
        }

        &:hover {
          background-color: color-mix(in srgb, $basicColor, var(--color-text-strong) 10%);
        }
      }

      &_blue {
        @include panelColors(var(--color-primary));
      }

      &_red {
        @include panelColors($red);
      }

      &_yellow {
        @include panelColors(var(--color-story));
      }

      &_lightblue {
        @include panelColors($blue);
      }

      &_green {
        @include panelColors(var(--color-community));
      }

      &_story {
        @include panelColors(var(--color-story));
      }

      &_goal {
        @include panelColors(var(--color-goal));
      }

      &_folder {
        @include panelColors(var(--color-folder));
      }


      color: var(--color-on-fill);

      text-align: center;

      @media (min-width: 400px) and (max-width: $screen-xs-max) {
        width: 8.125rem;
        min-height: 8.125rem;
      }
      @media (min-width: $screen-sm-min) and (max-width: $screen-sm-max) {
        width: 8.125rem;
        min-height: 8.125rem;
      }
      @media (min-width: $screen-md-min) {
        margin: 10px;
        width: 8.75rem;
        min-height: 8.75rem;
      }
    }

    &__button {
      height: 100%;
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
      justify-content: space-between;
      align-items: center;
      align-content: space-between;
    }

    &__title {
      font-size: 1.125rem;
    }

    &__icon-section {
      margin-top: 5px;
      width: 100%;
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;

      min-height: 5rem;
      @media (min-width: $screen-sm-min) and (max-width: $screen-sm-max) {
        min-height: 5.625rem;
      }
      @media (min-width: $screen-md-min) {
        min-height: 6.25rem;
      }

      &.user-lock {
        @include glyph-on-fill('/static/images/mm-icons/svg/user-lock');
        background-size: 70%;
      }

      &.user {
        @include glyph-on-fill('/static/images/overlays-icons/svg/user-add-one');
        background-size: 70%;
      }

      &.circle {
        @include glyph-on-fill('/static/images/overlays-icons/svg/my-circle');
        background-size: 70%;
      }

      &.groups {
        @include glyph-on-fill('/static/images/overlays-icons/svg/groups_new_white');
        background-size: 65%;
      }

      &.buddy-group {
        @include glyph-on-fill('/static/images/mm-icons/svg/np_buddies_88194_white_1');
        background-size: 65%;
      }

      &.communities {
        @include glyph-on-fill('/static/images/overlays-icons/svg/community_new_white');
        background-size: 65%;
      }

      &.wiki {
        @include glyph-on-fill('/static/images/new_daschboard_icons/2020/svg/mywiki-small-white');
        background-size: 65%;
      }

      &.diary {
        @include glyph-on-fill('/static/images/new_daschboard_icons/2020/svg/mydiary-white');
        background-size: 65%;
      }

      &.story {
        @include glyph-on-fill('/static/images/overlays-icons/svg/story_white_new');
        background-size: 65%;
      }

      &.goal {
        @include glyph-on-fill('/static/images/overlays-icons/svg/goals_white_new');
        background-size: 65%;
      }

      &.media {
        @include glyph-on-fill('/static/images/overlays-icons/svg/media-new');
        background-size: 65%;
      }

    }

    &__fa-icon {
      font-size: 3.75rem;
      @media (min-width: $screen-sm-min) and (max-width: $screen-sm-max) {
        font-size: 4.375rem;
        line-height: 5.625rem;
      }
      @media (min-width: $screen-md-min) {
        font-size: 5rem;
        line-height: 6.25rem;
      }
    }
  }
</style>
